/**
 * Graphiques en SVG ecrits a la main.
 *
 * Aucune bibliotheque de visualisation : la politique de securite du site
 * interdit tout script tiers et l'application doit fonctionner integralement
 * hors ligne. Le SVG inline satisfait les deux sans peser un octet de plus.
 *
 * ## Regles suivies
 *
 * - marques fines, extremites arrondies a 4 px ancrees a la ligne de base ;
 * - 2 px de fond entre deux marques voisines, jamais de contour pour separer ;
 * - grille et axes en filets pleins, jamais en pointilles ;
 * - pas d'etiquette sur chaque point : seuls les reperes utiles sont annotes ;
 * - **chaque graphique possede son equivalent en tableau**, car une infobulle
 *   ne doit jamais etre le seul moyen de lire une valeur — et il n'y a pas de
 *   survol sur un ecran tactile.
 *
 * Les couleurs viennent de la portee `.viz` de la feuille de style, ou elles
 * sont documentees et validees.
 */

import { useState, type ReactNode } from 'react'

import { FRISE_DEBUT_H, FRISE_DUREE_H, type BarreFrise } from '../analysis/statistiques'
import { formatDuree, formatJourCourt } from '../domain/time'

/**
 * Enveloppe commune : titre, bascule graphique/tableau.
 *
 * Le tableau n'est pas une option de confort, c'est la contrepartie du fait
 * qu'un doigt ne survole pas. Il est aussi ce qui autorise une teinte dont le
 * contraste sur le fond reste sous 3:1.
 */
export function Figure({
  titre,
  legende,
  tableau,
  children,
}: {
  titre: string
  legende?: ReactNode
  tableau: ReactNode
  children: ReactNode
}) {
  const [vueTableau, setVueTableau] = useState(false)
  return (
    <div className="figure">
      <div className="figure__entete">
        <h2>{titre}</h2>
        <button
          type="button"
          className="btn btn--fantome btn--petit"
          aria-pressed={vueTableau}
          onClick={() => setVueTableau((v) => !v)}
        >
          {vueTableau ? 'Graphique' : 'Chiffres'}
        </button>
      </div>
      {vueTableau ? <div className="defilable">{tableau}</div> : children}
      {!vueTableau && legende}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Frise des nuits
// ---------------------------------------------------------------------------

/**
 * Une ligne par nuit sur un axe 18 h -> 14 h.
 *
 * C'est le graphique signature de l'app : la regularite des horaires et la
 * fragmentation des nuits s'y lisent d'un seul coup d'oeil, ce qu'aucune
 * moyenne ne montre. Les reveils decomptes apparaissent comme des coupures
 * dans la barre — la nuit y ressemble a ce qu'elle a ete.
 */
export function FriseNuits({ barres }: { barres: (BarreFrise | null)[] }) {
  const hauteurLigne = 11
  const margeGauche = 34
  const margeHaut = 14
  const margeBas = 4
  const largeur = 320
  const largeurPlot = largeur - margeGauche - 4
  const hauteur = margeHaut + barres.length * hauteurLigne + margeBas

  // Graduations toutes les 4 h : au-dela l'axe devient illisible sur 320 px.
  const graduations: { x: number; libelle: string }[] = []
  for (let h = 0; h <= FRISE_DUREE_H; h += 4) {
    const heure = (FRISE_DEBUT_H + h) % 24
    graduations.push({
      x: margeGauche + (h / FRISE_DUREE_H) * largeurPlot,
      libelle: `${String(heure).padStart(2, '0')} h`,
    })
  }

  return (
    <svg
      className="viz"
      viewBox={`0 0 ${largeur} ${hauteur}`}
      role="img"
      aria-label={`Frise de ${barres.filter(Boolean).length} nuits`}
    >
      {graduations.map((g) => (
        <g key={g.x}>
          <line
            className="viz__grille"
            x1={g.x}
            y1={margeHaut - 3}
            x2={g.x}
            y2={hauteur - margeBas}
          />
          <text className="viz__tick" x={g.x} y={margeHaut - 6} textAnchor="middle">
            {g.libelle}
          </text>
        </g>
      ))}

      {barres.map((b, k) => {
        const y = margeHaut + k * hauteurLigne
        if (!b) return null
        const x0 = margeGauche + b.debut * largeurPlot
        const x1 = margeGauche + b.fin * largeurPlot
        const h = hauteurLigne - 3 // 2 px de fond entre deux lignes voisines

        return (
          <g key={b.date}>
            <text className="viz__tick" x={margeGauche - 5} y={y + h - 1} textAnchor="end">
              {formatJourCourt(b.date).slice(0, 3)}
            </text>
            <rect x={x0} y={y} width={Math.max(2, x1 - x0)} height={h} rx="3" fill="var(--viz-pose)" />
            {/* Les reveils sont perces dans la barre a la couleur de la
                surface, puis surlignes : une coupure se voit mieux qu'un
                changement de teinte. */}
            {b.trous.map((t, j) => {
              const tx0 = margeGauche + t.debut * largeurPlot
              const tx1 = margeGauche + t.fin * largeurPlot
              return (
                <rect
                  key={j}
                  x={tx0}
                  y={y}
                  width={Math.max(1.5, tx1 - tx0)}
                  height={h}
                  fill="var(--viz-reveil)"
                  stroke="var(--viz-surface)"
                  strokeWidth="1"
                />
              )
            })}
            <title>{`${formatJourCourt(b.date)} : ${b.trous.length} réveil(s)`}</title>
          </g>
        )
      })}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Barres verticales
// ---------------------------------------------------------------------------

export interface PointBarre {
  libelle: string
  /** `null` = pas de mesure ce jour-la. Ce n'est **pas** un zero. */
  valeur: number | null
  info?: string
}

/** Barre a sommet arrondi, ancree a la ligne de base. */
function cheminBarre(x: number, y: number, l: number, h: number): string {
  if (h <= 0) return ''
  const r = Math.min(4, l / 2, h)
  return `M${x},${y + h}L${x},${y + r}Q${x},${y} ${x + r},${y}L${x + l - r},${y}Q${x + l},${y} ${x + l},${y + r}L${x + l},${y + h}Z`
}

/**
 * Duree par nuit, avec ligne d'objectif.
 *
 * Les jours sans mesure laissent un vide, pas une barre a zero : confondre les
 * deux ferait croire a une nuit blanche a chaque automatisation ratee.
 */
export function BarresDuree({
  points,
  objectifMs,
}: {
  points: PointBarre[]
  objectifMs: number
}) {
  const largeur = 320
  const hauteur = 120
  const margeBas = 16
  const margeGauche = 30
  const hPlot = hauteur - margeBas
  const largeurPlot = largeur - margeGauche - 4

  const valeurs = points.map((p) => p.valeur).filter((v): v is number => v !== null)
  const max = Math.max(objectifMs, ...valeurs, 1)
  const y = (v: number) => hPlot - (v / max) * (hPlot - 6)

  const pas = largeurPlot / Math.max(1, points.length)
  const largeurBarre = Math.max(2, pas - 2) // 2 px de fond entre voisines
  const pasEtiquette = Math.ceil(points.length / 6)

  return (
    <svg
      className="viz"
      viewBox={`0 0 ${largeur} ${hauteur}`}
      role="img"
      aria-label="Temps téléphone posé par nuit"
    >
      <line className="viz__axe" x1={margeGauche} y1={hPlot} x2={largeur - 4} y2={hPlot} />
      <line
        className="viz__objectif"
        x1={margeGauche}
        y1={y(objectifMs)}
        x2={largeur - 4}
        y2={y(objectifMs)}
      />
      <text className="viz__tick" x={margeGauche - 4} y={y(objectifMs) + 3} textAnchor="end">
        {formatDuree(objectifMs)}
      </text>

      {points.map((p, k) => {
        const x = margeGauche + k * pas + 1
        if (p.valeur === null) return null
        const h = hPlot - y(p.valeur)
        return (
          <g key={p.libelle}>
            <path d={cheminBarre(x, y(p.valeur), largeurBarre, h)} fill="var(--viz-pose)" />
            {/* Zone de survol elargie : une barre de 4 px est trop fine a viser. */}
            <rect x={x - 2} y={0} width={largeurBarre + 4} height={hPlot} fill="transparent">
              <title>{p.info ?? `${p.libelle} : ${formatDuree(p.valeur)}`}</title>
            </rect>
          </g>
        )
      })}

      {points.map((p, k) =>
        k % pasEtiquette === 0 ? (
          <text
            key={`e${p.libelle}`}
            className="viz__tick"
            x={margeGauche + k * pas + largeurBarre / 2 + 1}
            y={hauteur - 4}
            textAnchor="middle"
          >
            {p.libelle}
          </text>
        ) : null,
      )}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Barres horizontales
// ---------------------------------------------------------------------------

/** Comparaison par categorie. En divs plutot qu'en SVG : le texte s'y aligne
 *  naturellement et le tableau equivalent reste a portee. */
export function BarresHorizontales({
  lignes,
  max,
}: {
  lignes: { libelle: string; valeur: number | null; detail?: string }[]
  max: number
}) {
  return (
    <div className="pile pile--serree">
      {lignes.map((l) => (
        <div key={l.libelle}>
          <div className="ligne ligne--espacee">
            <span className="doux">{l.libelle}</span>
            <span className="doux mono">
              {l.valeur === null ? '—' : formatDuree(l.valeur)}
              {l.detail && <span className="tres-doux"> · {l.detail}</span>}
            </span>
          </div>
          <div className="jauge">
            <div
              className="jauge__part"
              style={{ width: l.valeur === null ? 0 : `${(l.valeur / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}
